export type PreviewStateLike = {
  domain: string;
  slugs: string[];
  defaultPort?: number;
  ports?: number[];
};

export const FALLBACK_PREVIEW_PORT = 3000;

export function getDefaultPreviewPort(preview: PreviewStateLike | null | undefined): number {
  const port = preview?.defaultPort;
  return Number.isFinite(port) && (port as number) > 0
    ? (port as number)
    : FALLBACK_PREVIEW_PORT;
}

export function getLivePreviewPorts(preview: PreviewStateLike | null | undefined): number[] {
  if (!preview?.ports?.length) return [];

  return preview.ports.filter((port): port is number =>
    Number.isInteger(port) && port > 0,
  );
}

export function hasAnyLivePreview(preview: PreviewStateLike | null | undefined): boolean {
  return getLivePreviewPorts(preview).length > 0;
}

export function hasMultipleLivePreviewPorts(preview: PreviewStateLike | null | undefined): boolean {
  return getLivePreviewPorts(preview).length > 1;
}

export function isPreviewPortLive(
  preview: PreviewStateLike | null | undefined,
  port: number | null | undefined,
): boolean {
  if (!Number.isInteger(port) || (port as number) <= 0) return false;
  return getLivePreviewPorts(preview).includes(port as number);
}

export function getAutoPreviewPort(
  preview: PreviewStateLike | null | undefined,
  selectedPort?: number | null,
): number | null {
  const livePorts = getLivePreviewPorts(preview);
  if (!livePorts.length) return null;

  if (isPreviewPortLive(preview, selectedPort)) {
    return selectedPort as number;
  }

  const defaultPort = getDefaultPreviewPort(preview);
  if (livePorts.includes(defaultPort)) {
    return defaultPort;
  }

  if (livePorts.length === 1) {
    return livePorts[0];
  }

  return null;
}

export function getPreviewHost(
  preview: PreviewStateLike | null | undefined,
  selectedPort?: number | null,
): string | null {
  if (!preview?.domain || !preview.slugs?.length) return null;

  const port = getAutoPreviewPort(preview, selectedPort);
  if (port === null) return null;

  if (preview.domain === "localhost" || preview.domain === "127.0.0.1") {
    return `${preview.domain}:${port}`;
  }

  const slug = preview.slugs[0];
  const defaultPort = getDefaultPreviewPort(preview);
  return port === defaultPort
    ? `${slug}.${preview.domain}`
    : `${slug}-${port}.${preview.domain}`;
}

export function getPreviewUrl(
  preview: PreviewStateLike | null | undefined,
  selectedPort?: number | null,
): string | null {
  const host = getPreviewHost(preview, selectedPort);
  if (!host) return null;
  if (preview?.domain === "localhost" || preview?.domain === "127.0.0.1") {
    return `http://${host}`;
  }
  return `https://${host}`;
}
